/**
 * F-03E-2 — Server-backed dock mission store.
 *
 * Mission state (rows + waypoints + plan_json) round-trips through the
 * F-03 backend via `services/dockApi` + `services/dockMissionAdapter`.
 * CRUD uses optimistic local updates with `If-Match` / `version`
 * concurrency; on 409 the affected mission is re-fetched from the server
 * and replaces the local copy (server-wins rebase). Per-mission mutations
 * serialize through `flushMissionSave` (P-31 latest-intent pattern from
 * `projectStore.flushOverlayVisibility`) so a burst of edits collapses
 * into one in-flight PATCH plus one pending re-fire.
 *
 * What stays LOCAL (per-device, NOT in the backend):
 *   • Dock stations + selected dock + no-fly zones — persisted to
 *     `dock-demo:stations:{siteId}`. The backend has no `dock_stations`
 *     table yet — they remain a per-device local concept.
 *   • Demo / lifecycle / live-view fields on missions (`flowState`,
 *     `sentAt`, `mockDataFolderUrl`, `liveViewMissionId`, `importedPhotos`,
 *     etc.) — these are demo-flow state, not part of the F-03 row contract.
 *   • Transient UI state (placement / hover / selection / scan-draw /
 *     NFZ-draw / clipboard / context menu).
 *
 * Legacy storage migrations:
 *   • v1 (`dock-demo:state-v1`, single global blob): `dockStations` copied
 *     into the active project's bucket on first load post-upgrade; v1 key
 *     is then deleted.
 *   • v2 (`dock-demo:state-v2:{siteId}`, per-site blob with mission drafts):
 *     `dockStations` copied into the new key; **mission drafts in v2 are
 *     NOT auto-uploaded** — they remain in v2 as orphaned data (a future
 *     migration could expose a "publish v2 drafts" action). The v2 key is
 *     left intact; new persistence writes to `dock-demo:stations:{siteId}`.
 *
 * Filename + hook export remain `dockDemoStore` / `useDockDemoStore` — a
 * rename would force every dock-demo UI module to update imports for no
 * functional gain.
 */

import { create } from 'zustand';
import type {
  DemoMission,
  DemoPhoto,
  DemoVideo,
  DemoFlightPath,
  DemoWaypoint,
  DemoMissionFinishAction,
  DemoHeadingMode,
  DemoDockStation,
  DemoLegParams,
  DemoNoFlyZone,
} from '../components/dock-demo/types';
import {
  listDockMissions,
  getDockMission,
  createDockMission,
  updateDockMission,
  deleteDockMission,
  isConflictError,
  ApiError,
} from '../services/dockApi';
import {
  backendRowToDemoMission,
  demoMissionToCreatePayload,
  demoMissionToUpdatePayload,
  DockMissionAdapterError,
} from '../services/dockMissionAdapter';
import { useShareSessionStore } from './shareSessionStore';
import { useAuthStore } from './authStore';
import { isShareViewerState } from '../hooks/useShareViewer';

// ─── Mission presets ──────────────────────────────────────────────────────

export type MissionPresetKey =
  | 'thermal-sweep'
  | 'visual-overview'
  | 'detailed-close-up'
  | 'boundary-patrol';

interface MissionPresetConfig {
  label: string;
  description: string;
  defaultAlt: number;
  defaultGimbalPitch: number;
  globalSpeedMps: number;
  defaultLegParams: DemoLegParams;
}

const MISSION_PRESETS: Record<MissionPresetKey, MissionPresetConfig> = {
  'thermal-sweep': {
    label: 'Thermal sweep',
    description: 'Paired wide-RGB + thermal interval shots every 5 m with zoom video',
    defaultAlt: 30,
    defaultGimbalPitch: -45,
    globalSpeedMps: 5,
    defaultLegParams: {
      gimbalPitch: -45,
      recordVideo: true,
      recordVideoLens: 'zoom',
      intervalPhoto: { enabled: true, distanceM: 5, lenses: ['wide', 'ir'] },
    },
  },
  'visual-overview': {
    label: 'Visual overview',
    description: 'Nadir RGB pass at 60 m, wide interval every 8 m',
    defaultAlt: 60,
    defaultGimbalPitch: -90,
    globalSpeedMps: 7,
    defaultLegParams: {
      gimbalPitch: -90,
      recordVideo: true,
      recordVideoLens: 'wide',
      intervalPhoto: { enabled: true, distanceM: 8, lenses: ['wide'] },
    },
  },
  'detailed-close-up': {
    label: 'Detailed close-up',
    description: 'Low-altitude zoom + thermal AB capture every 3 m',
    defaultAlt: 15,
    defaultGimbalPitch: -30,
    globalSpeedMps: 3,
    defaultLegParams: {
      gimbalPitch: -30,
      recordVideo: true,
      recordVideoLens: 'zoom',
      intervalPhoto: { enabled: true, distanceM: 3, lenses: ['zoom', 'ir'] },
    },
  },
  'boundary-patrol': {
    label: 'Boundary patrol',
    description: 'Slight downward gimbal, wide interval every 10 m, no video',
    defaultAlt: 25,
    defaultGimbalPitch: -15,
    globalSpeedMps: 6,
    defaultLegParams: {
      gimbalPitch: -15,
      recordVideo: false,
      intervalPhoto: { enabled: true, distanceM: 10, lenses: ['wide'] },
    },
  },
};

export function listMissionPresets(): Array<{
  key: MissionPresetKey;
  label: string;
  description: string;
}> {
  return (Object.keys(MISSION_PRESETS) as MissionPresetKey[]).map((k) => ({
    key: k,
    label: MISSION_PRESETS[k].label,
    description: MISSION_PRESETS[k].description,
  }));
}

// ─── Local persistence (dock stations only — missions are server-backed) ─

const STORAGE_KEY_PREFIX = 'dock-demo:stations';
const LEGACY_STATE_V2_PREFIX = 'dock-demo:state-v2';
const LEGACY_GLOBAL_V1_KEY = 'dock-demo:state-v1';

function storageKey(siteId: string): string {
  return `${STORAGE_KEY_PREFIX}:${siteId}`;
}
function legacyV2Key(siteId: string): string {
  return `${LEGACY_STATE_V2_PREFIX}:${siteId}`;
}

interface PersistedDockState {
  dockStations: DemoDockStation[];
  selectedDockStationId: string | null;
}

const EMPTY_DOCK_STATE: PersistedDockState = {
  dockStations: [],
  selectedDockStationId: null,
};

function loadPersistedDock(siteId: string): PersistedDockState {
  try {
    const raw = localStorage.getItem(storageKey(siteId));
    if (!raw) return EMPTY_DOCK_STATE;
    const parsed = JSON.parse(raw) as Partial<PersistedDockState>;
    return {
      dockStations: parsed.dockStations ?? [],
      selectedDockStationId: parsed.selectedDockStationId ?? null,
    };
  } catch {
    return EMPTY_DOCK_STATE;
  }
}

function persistDock(state: PersistedDockState, siteId: string | null): void {
  if (siteId === null) return;
  try {
    localStorage.setItem(storageKey(siteId), JSON.stringify(state));
  } catch {
    // quota exceeded / storage disabled — best-effort
  }
}

/**
 * One-time copy of legacy v1 / v2 dockStations into the new
 * `dock-demo:stations:{siteId}` bucket. v2 mission drafts are
 * intentionally NOT migrated — they remain in v2 as orphaned data so a
 * future opt-in "publish drafts" tool can recover them without an
 * unprompted upload on this upgrade.
 */
function migrateLegacyIfNeeded(siteId: string): void {
  try {
    if (localStorage.getItem(storageKey(siteId))) return;

    const v2 = localStorage.getItem(legacyV2Key(siteId));
    if (v2) {
      try {
        const parsed = JSON.parse(v2) as {
          dockStations?: DemoDockStation[];
          selectedDockStationId?: string | null;
        };
        persistDock(
          {
            dockStations: parsed.dockStations ?? [],
            selectedDockStationId: parsed.selectedDockStationId ?? null,
          },
          siteId,
        );
        return;
      } catch {
        // fall through to v1
      }
    }

    const v1 = localStorage.getItem(LEGACY_GLOBAL_V1_KEY);
    if (v1) {
      try {
        const parsed = JSON.parse(v1) as {
          dockStations?: DemoDockStation[];
          selectedDockStationId?: string | null;
        };
        persistDock(
          {
            dockStations: parsed.dockStations ?? [],
            selectedDockStationId: parsed.selectedDockStationId ?? null,
          },
          siteId,
        );
        localStorage.removeItem(LEGACY_GLOBAL_V1_KEY);
      } catch {
        // ignore — best-effort
      }
    }
  } catch {
    // ignore — localStorage may be disabled
  }
}

function genId(prefix: string): string {
  return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}

// ─── Store shape ─────────────────────────────────────────────────────────

interface DockDemoState {
  currentSiteId: string | null;

  // Mission state (server-backed)
  missions: DemoMission[];
  selectedMissionId: string | null;
  /** Server-assigned `version` per mission. Missing iff the mission has
   *  not been created server-side yet (temp id). */
  missionVersions: Record<string, number>;
  /** True while a save (POST or PATCH) is in flight for this mission id. */
  missionSavingById: Record<string, true>;
  /** True iff a save was requested while another save for the same
   *  mission was already in flight — the post-resolve finally block
   *  drains this flag by re-firing flushMissionSave. */
  missionPendingSaveById: Record<string, true>;
  /** True iff the most recent save resulted in a 409 and the mission was
   *  reloaded from the server. */
  missionConflictById: Record<string, true>;
  /** Last error message per mission. Cleared at the start of each save
   *  attempt; null after a successful save. */
  missionLastErrorById: Record<string, string | null>;
  /** True while initial hydration (list + per-mission GET) is in flight. */
  missionsLoading: boolean;
  /** True after the first hydration attempt completes (success or error). */
  missionsLoaded: boolean;
  /** Last hydration error message — null on success. */
  missionsError: string | null;

  // Local-only state
  dockStations: DemoDockStation[];
  selectedDockStationId: string | null;
  placementMode: boolean;
  selectedDemoPhotoId: string | null;
  nfzDrawMode: boolean;
  nfzDrawVertices: [number, number][];
  nfzEditingId: string | null;
  hoveredWaypointId: string | null;
  selectedWaypointId: string | null;
  clipboardLegParams: DemoLegParams | null;
  contextMenuTarget: {
    missionId: string;
    waypointId: string;
    x: number;
    y: number;
  } | null;
  scanDrawMode: 'polygon' | 'line' | null;
  scanDrawVertices: [number, number][];
  scanLineSpacingM: number;
  scanLineStepM: number;
  liveViewMissionId: string | null;

  // ── Actions ─────────────────────────────────────────────────────────
  setActiveProject: (siteId: string | null) => void;

  createMission: (name?: string) => string;
  createMissionFromPreset: (preset: MissionPresetKey) => string;
  deleteMission: (id: string) => void;
  selectMission: (id: string | null) => void;
  updateMission: (
    id: string,
    patch: Partial<Omit<DemoMission, 'id' | 'createdAt'>>,
  ) => void;

  setPlacementMode: (on: boolean) => void;
  addWaypoint: (missionId: string, lng: number, lat: number) => void;
  updateWaypoint: (
    missionId: string,
    waypointId: string,
    patch: Partial<DemoWaypoint>,
  ) => void;
  updateWaypointLegParams: (
    missionId: string,
    waypointId: string,
    patch: Partial<DemoLegParams>,
  ) => void;
  deleteWaypoint: (missionId: string, waypointId: string) => void;
  reorderWaypoint: (
    missionId: string,
    fromIndex: number,
    toIndex: number,
  ) => void;
  insertWaypointAt: (
    missionId: string,
    atIndex: number,
    lng: number,
    lat: number,
  ) => string | null;
  duplicateWaypoint: (
    missionId: string,
    waypointId: string,
  ) => string | null;
  addWaypointsBulk: (
    missionId: string,
    points: Array<{ lng: number; lat: number }>,
  ) => string[];

  setScanDrawMode: (mode: 'polygon' | 'line' | null) => void;
  appendScanDrawVertex: (lng: number, lat: number) => void;
  clearScanDraft: () => void;
  setScanLineSpacingM: (m: number) => void;
  setScanLineStepM: (m: number) => void;

  setHoveredWaypointId: (id: string | null) => void;
  setSelectedWaypointId: (id: string | null) => void;
  setClipboardLegParams: (params: DemoLegParams | null) => void;
  setContextMenuTarget: (target: DockDemoState['contextMenuTarget']) => void;

  setMissionImportedData: (
    missionId: string,
    data: {
      photos: DemoPhoto[];
      videos: DemoVideo[];
      flightPath: DemoFlightPath | null;
    },
  ) => void;
  clearMissionImportedData: (missionId: string) => void;

  setSelectedDemoPhoto: (id: string | null) => void;

  createDockStation: (name?: string) => string;
  deleteDockStation: (id: string) => void;
  selectDockStation: (id: string | null) => void;
  updateDockStation: (id: string, patch: Partial<DemoDockStation>) => void;

  setNfzDrawMode: (on: boolean) => void;
  appendNfzDrawVertex: (lng: number, lat: number) => void;
  clearNfzDraft: () => void;
  setNfzEditingId: (id: string | null) => void;
  addNoFlyZone: (
    dockId: string,
    vertices: [number, number][],
    name?: string,
  ) => string | null;
  updateNoFlyZone: (
    dockId: string,
    nfzId: string,
    patch: Partial<Omit<DemoNoFlyZone, 'id'>>,
  ) => void;
  deleteNoFlyZone: (dockId: string, nfzId: string) => void;

  removeAutoRoutedWaypoints: (missionId: string) => number;

  openLiveView: (missionId: string) => void;
  closeLiveView: () => void;

  sendMissionToOperator: (missionId: string) => void;
  cancelMissionFlow: (missionId: string) => void;
  setMissionFlowState: (
    missionId: string,
    state: import('../components/dock-demo/types').DemoMissionFlowState,
    extras?: { scheduledStartAt?: number | null },
  ) => void;

  clearAll: () => void;
}

function snapshotDockState(s: DockDemoState): PersistedDockState {
  return {
    dockStations: s.dockStations,
    selectedDockStationId: s.selectedDockStationId,
  };
}

const DEFAULT_MISSION_FIELDS = {
  globalSpeedMps: 5,
  finishAction: 'goHome' as DemoMissionFinishAction,
  headingMode: 'auto' as DemoHeadingMode,
  targetDockModel: 'dock2' as const,
};

type MissionStateFields = Pick<
  DockDemoState,
  | 'missions'
  | 'selectedMissionId'
  | 'missionVersions'
  | 'missionSavingById'
  | 'missionPendingSaveById'
  | 'missionConflictById'
  | 'missionLastErrorById'
  | 'missionsLoading'
  | 'missionsLoaded'
  | 'missionsError'
>;

const INITIAL_MISSION_STATE: MissionStateFields = {
  missions: [],
  selectedMissionId: null,
  missionVersions: {},
  missionSavingById: {},
  missionPendingSaveById: {},
  missionConflictById: {},
  missionLastErrorById: {},
  missionsLoading: false,
  missionsLoaded: false,
  missionsError: null,
};

type TransientFields = Pick<
  DockDemoState,
  | 'placementMode'
  | 'selectedDemoPhotoId'
  | 'nfzDrawMode'
  | 'nfzDrawVertices'
  | 'nfzEditingId'
  | 'hoveredWaypointId'
  | 'selectedWaypointId'
  | 'clipboardLegParams'
  | 'contextMenuTarget'
  | 'scanDrawMode'
  | 'scanDrawVertices'
  | 'scanLineSpacingM'
  | 'scanLineStepM'
  | 'liveViewMissionId'
>;

const INITIAL_TRANSIENT: TransientFields = {
  placementMode: false,
  selectedDemoPhotoId: null,
  nfzDrawMode: false,
  nfzDrawVertices: [],
  nfzEditingId: null,
  hoveredWaypointId: null,
  selectedWaypointId: null,
  clipboardLegParams: null,
  contextMenuTarget: null,
  scanDrawMode: null,
  scanDrawVertices: [],
  scanLineSpacingM: 15,
  scanLineStepM: 10,
  liveViewMissionId: null,
};

// ─── Save plumbing (private to this module) ──────────────────────────────

function omitKey<T>(obj: Record<string, T>, key: string): Record<string, T> {
  if (!(key in obj)) return obj;
  const next = { ...obj };
  delete next[key];
  return next;
}

function extractErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

/**
 * PATCH the mission's current local state to the backend. Optimistic-
 * concurrency via `If-Match: missionVersions[id]`. On 409, the mission is
 * refetched and replaces the local copy (server-wins rebase) and the
 * `missionConflictById` flag is set so the UI can render a "your view
 * was reloaded" banner. P-31 pattern: while one save is in flight,
 * latest-intent mutations set `missionPendingSaveById[id]`; the finally
 * block drains the flag with a single re-fire on the freshest state.
 */
async function flushMissionSave(missionId: string): Promise<void> {
  const s0 = useDockDemoStore.getState();
  const siteId = s0.currentSiteId;
  if (!siteId) return;
  const mission = s0.missions.find((m) => m.id === missionId);
  if (!mission) return;
  const version = s0.missionVersions[missionId];
  if (!version) return; // not yet server-created — caller should use createOnServer

  const activeDock =
    s0.dockStations.find((d) => d.id === mission.dockStationId) ?? null;

  let payload;
  try {
    payload = demoMissionToUpdatePayload(mission, activeDock);
  } catch (err) {
    if (
      err instanceof DockMissionAdapterError
      && err.code === 'EMPTY_WAYPOINTS'
    ) {
      // Mission was valid earlier but is now empty (user deleted all
      // waypoints). Backend would reject the empty plan. Leave the row
      // intact server-side; surface a hint locally — next waypoint add
      // will retry.
      useDockDemoStore.setState((s) => ({
        missionLastErrorById: {
          ...s.missionLastErrorById,
          [missionId]: 'Mission has no waypoints — add at least one to save.',
        },
      }));
      return;
    }
    useDockDemoStore.setState((s) => ({
      missionLastErrorById: {
        ...s.missionLastErrorById,
        [missionId]: extractErrorMessage(err),
      },
    }));
    return;
  }

  useDockDemoStore.setState((s) => ({
    missionSavingById: { ...s.missionSavingById, [missionId]: true },
    missionLastErrorById: { ...s.missionLastErrorById, [missionId]: null },
  }));

  try {
    const result = await updateDockMission(siteId, missionId, payload, version);
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    useDockDemoStore.setState((s) => {
      if (!s.missions.some((m) => m.id === missionId)) return s;
      return {
        missionVersions: { ...s.missionVersions, [missionId]: result.version },
        missionSavingById: omitKey(s.missionSavingById, missionId),
      };
    });
  } catch (err) {
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    if (isConflictError(err)) {
      try {
        const fresh = await getDockMission(siteId, missionId);
        if (useDockDemoStore.getState().currentSiteId !== siteId) return;
        useDockDemoStore.setState((s) => {
          if (!s.missions.some((m) => m.id === missionId)) return s;
          const refreshed = backendRowToDemoMission(fresh.data);
          return {
            missions: s.missions.map((m) =>
              m.id === missionId ? refreshed : m,
            ),
            missionVersions: {
              ...s.missionVersions,
              [missionId]: fresh.version,
            },
            missionSavingById: omitKey(s.missionSavingById, missionId),
            missionConflictById: {
              ...s.missionConflictById,
              [missionId]: true,
            },
            missionLastErrorById: {
              ...s.missionLastErrorById,
              [missionId]:
                'Server has a newer version — your view was reloaded. Re-apply your edits and save again.',
            },
          };
        });
      } catch (refetchErr) {
        useDockDemoStore.setState((s) => ({
          missionSavingById: omitKey(s.missionSavingById, missionId),
          missionLastErrorById: {
            ...s.missionLastErrorById,
            [missionId]: extractErrorMessage(refetchErr),
          },
        }));
      }
    } else {
      useDockDemoStore.setState((s) => ({
        missionSavingById: omitKey(s.missionSavingById, missionId),
        missionLastErrorById: {
          ...s.missionLastErrorById,
          [missionId]: extractErrorMessage(err),
        },
      }));
    }
  } finally {
    const post = useDockDemoStore.getState();
    if (
      post.currentSiteId === siteId
      && post.missionPendingSaveById[missionId]
      && post.missionVersions[missionId]
    ) {
      useDockDemoStore.setState((s) => ({
        missionPendingSaveById: omitKey(s.missionPendingSaveById, missionId),
      }));
      void flushMissionSave(missionId);
    }
  }
}

/**
 * POST the (temp-id) mission to the backend and swap its id to the
 * server-assigned uuid. Empty-waypoint missions defer the POST until the
 * first waypoint addition triggers a retry through `scheduleMissionSave`.
 */
async function createOnServer(missionId: string): Promise<void> {
  const s0 = useDockDemoStore.getState();
  const siteId = s0.currentSiteId;
  if (!siteId) return;
  if (s0.missionSavingById[missionId]) return;
  if (s0.missionVersions[missionId]) return; // already created server-side
  const mission = s0.missions.find((m) => m.id === missionId);
  if (!mission) return;

  const activeDock =
    s0.dockStations.find((d) => d.id === mission.dockStationId) ?? null;

  let payload;
  try {
    payload = demoMissionToCreatePayload(mission, activeDock);
  } catch (err) {
    if (
      err instanceof DockMissionAdapterError
      && err.code === 'EMPTY_WAYPOINTS'
    ) {
      // Defer the POST until the first waypoint is added. addWaypoint →
      // scheduleMissionSave → createOnServer will retry, and by then the
      // adapter will accept the payload.
      return;
    }
    useDockDemoStore.setState((s) => ({
      missionLastErrorById: {
        ...s.missionLastErrorById,
        [missionId]: extractErrorMessage(err),
      },
    }));
    return;
  }

  useDockDemoStore.setState((s) => ({
    missionSavingById: { ...s.missionSavingById, [missionId]: true },
    missionLastErrorById: { ...s.missionLastErrorById, [missionId]: null },
  }));

  try {
    const result = await createDockMission(siteId, payload);
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    const serverId = result.data.id;
    useDockDemoStore.setState((s) => {
      const tempMission = s.missions.find((m) => m.id === missionId);
      if (!tempMission) return s;
      // Adopt the server id + canonical timestamps. Keep the user's
      // current waypoints + demo fields — if the user kept editing during
      // the round trip, the pending flag below will trigger another PATCH
      // that flushes the divergence.
      const replaced: DemoMission = {
        ...tempMission,
        id: serverId,
        createdAt: result.data.created_at,
        updatedAt: result.data.updated_at,
      };
      const hadPending = !!s.missionPendingSaveById[missionId];
      return {
        missions: s.missions.map((m) => (m.id === missionId ? replaced : m)),
        selectedMissionId:
          s.selectedMissionId === missionId ? serverId : s.selectedMissionId,
        missionVersions: {
          ...omitKey(s.missionVersions, missionId),
          [serverId]: result.version,
        },
        missionSavingById: omitKey(s.missionSavingById, missionId),
        missionPendingSaveById: hadPending
          ? {
              ...omitKey(s.missionPendingSaveById, missionId),
              [serverId]: true,
            }
          : omitKey(s.missionPendingSaveById, missionId),
      };
    });
    const post = useDockDemoStore.getState();
    if (post.missionPendingSaveById[serverId]) {
      useDockDemoStore.setState((s) => ({
        missionPendingSaveById: omitKey(s.missionPendingSaveById, serverId),
      }));
      void flushMissionSave(serverId);
    }
  } catch (err) {
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    useDockDemoStore.setState((s) => ({
      missionSavingById: omitKey(s.missionSavingById, missionId),
      missionLastErrorById: {
        ...s.missionLastErrorById,
        [missionId]: extractErrorMessage(err),
      },
    }));
  }
}

function scheduleMissionSave(missionId: string): void {
  const s = useDockDemoStore.getState();
  if (!s.currentSiteId) return;
  if (!s.missions.some((m) => m.id === missionId)) return;
  if (s.missionSavingById[missionId]) {
    useDockDemoStore.setState((curr) => ({
      missionPendingSaveById: {
        ...curr.missionPendingSaveById,
        [missionId]: true,
      },
    }));
    return;
  }
  if (!s.missionVersions[missionId]) {
    void createOnServer(missionId);
  } else {
    void flushMissionSave(missionId);
  }
}

async function hydrateMissionsFromBackend(siteId: string): Promise<void> {
  // F-03E-5: defensive short-circuit for share viewers. The backend's
  // `/dock-missions` routes are not `shareReadable` (visitors get 403),
  // so firing the list call only buys us a misleading "Could not load
  // saved dock missions" banner. Use the same `isShareViewerState`
  // predicate that drives `useIsShareViewer` / `useIsReadOnlyViewer`
  // (pure function, no React) so the UI rail gate and this network gate
  // can never disagree. EditorPage already hides the rail icons + panel
  // mount in share mode (F-03E-5), but a future code path (URL hack,
  // legacy deep-link, programmatic setActiveProject from a non-EditorPage
  // entry) could still hit this; the guard makes that safe by leaving
  // the store in the "loaded with no missions" state without firing any
  // backend call.
  const shareActive = useShareSessionStore.getState().active;
  const authToken = useAuthStore.getState().token;
  if (isShareViewerState(shareActive, authToken)) {
    useDockDemoStore.setState({
      missions: [],
      missionVersions: {},
      missionsLoading: false,
      missionsLoaded: true,
      missionsError: null,
    });
    return;
  }

  useDockDemoStore.setState({
    missionsLoading: true,
    missionsLoaded: false,
    missionsError: null,
  });
  try {
    const summaries = await listDockMissions(siteId);
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    if (summaries.length === 0) {
      useDockDemoStore.setState((s) => {
        // Preserve any temp-id missions the user created during hydration.
        const preserved = s.missions.filter((m) => !s.missionVersions[m.id]);
        return {
          missions: preserved,
          missionVersions: {},
          missionsLoading: false,
          missionsLoaded: true,
          missionsError: null,
        };
      });
      return;
    }
    const results = await Promise.all(
      summaries.map((sum) =>
        getDockMission(siteId, sum.id).catch(() => null),
      ),
    );
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    const serverMissions: DemoMission[] = [];
    const versions: Record<string, number> = {};
    for (const r of results) {
      if (!r) continue;
      try {
        const m = backendRowToDemoMission(r.data);
        serverMissions.push(m);
        versions[m.id] = r.version;
      } catch {
        // skip malformed rows
      }
    }
    useDockDemoStore.setState((s) => {
      const serverIds = new Set(serverMissions.map((m) => m.id));
      // Preserve any temp-id missions the user created during hydration —
      // their server-create POST is still in flight.
      const preservedLocal = s.missions.filter(
        (m) => !s.missionVersions[m.id] && !serverIds.has(m.id),
      );
      return {
        missions: [...serverMissions, ...preservedLocal],
        missionVersions: versions,
        missionsLoading: false,
        missionsLoaded: true,
        missionsError: null,
      };
    });
  } catch (err) {
    if (useDockDemoStore.getState().currentSiteId !== siteId) return;
    useDockDemoStore.setState({
      missions: [],
      missionVersions: {},
      missionsLoading: false,
      missionsLoaded: true,
      missionsError: extractErrorMessage(err),
    });
  }
}

// ─── Store ───────────────────────────────────────────────────────────────

export const useDockDemoStore = create<DockDemoState>((set) => ({
  currentSiteId: null,
  dockStations: [],
  selectedDockStationId: null,
  ...INITIAL_MISSION_STATE,
  ...INITIAL_TRANSIENT,

  setActiveProject: (siteId) => {
    const prev = useDockDemoStore.getState();
    if (prev.currentSiteId === siteId) return;
    if (prev.currentSiteId !== null) {
      persistDock(snapshotDockState(prev), prev.currentSiteId);
    }
    if (siteId !== null) migrateLegacyIfNeeded(siteId);
    const localDock =
      siteId !== null ? loadPersistedDock(siteId) : EMPTY_DOCK_STATE;

    set({
      currentSiteId: siteId,
      dockStations: localDock.dockStations,
      selectedDockStationId: localDock.selectedDockStationId,
      ...INITIAL_MISSION_STATE,
      missionsLoading: siteId !== null,
      ...INITIAL_TRANSIENT,
    });

    if (siteId !== null) {
      void hydrateMissionsFromBackend(siteId);
    }
  },

  createMission: (name = 'Untitled mission') => {
    const now = new Date().toISOString();
    const s0 = useDockDemoStore.getState();
    const selDockId = (() => {
      const sel = s0.selectedDockStationId;
      if (sel && s0.dockStations.some((d) => d.id === sel)) return sel;
      return s0.dockStations[0]?.id ?? null;
    })();
    const tempId = genId('mission');
    const mission: DemoMission = {
      id: tempId,
      name,
      waypoints: [],
      ...DEFAULT_MISSION_FIELDS,
      dockStationId: selDockId,
      visible: true,
      importedDataVisible: true,
      flowState: 'draft',
      createdAt: now,
      updatedAt: now,
    };
    set((s) => ({
      missions: [...s.missions, mission],
      selectedMissionId: tempId,
    }));
    if (s0.currentSiteId !== null) void createOnServer(tempId);
    return tempId;
  },

  createMissionFromPreset: (preset) => {
    const config = MISSION_PRESETS[preset];
    const now = new Date().toISOString();
    const s0 = useDockDemoStore.getState();
    const selDockId = (() => {
      const sel = s0.selectedDockStationId;
      if (sel && s0.dockStations.some((d) => d.id === sel)) return sel;
      return s0.dockStations[0]?.id ?? null;
    })();
    const tempId = genId('mission');
    set((s) => {
      const sameKindCount = s.missions.filter((m) =>
        m.name.startsWith(config.label),
      ).length;
      const name =
        sameKindCount === 0
          ? config.label
          : `${config.label} ${sameKindCount + 1}`;
      const mission: DemoMission = {
        id: tempId,
        name,
        waypoints: [],
        ...DEFAULT_MISSION_FIELDS,
        globalSpeedMps: config.globalSpeedMps,
        dockStationId: selDockId,
        visible: true,
        importedDataVisible: true,
        flowState: 'draft',
        defaultAlt: config.defaultAlt,
        defaultGimbalPitch: config.defaultGimbalPitch,
        defaultLegParams: { ...config.defaultLegParams },
        createdAt: now,
        updatedAt: now,
      };
      return {
        missions: [...s.missions, mission],
        selectedMissionId: tempId,
      };
    });
    if (s0.currentSiteId !== null) void createOnServer(tempId);
    return tempId;
  },

  deleteMission: (id) => {
    const s0 = useDockDemoStore.getState();
    const snapshot = s0.missions.find((m) => m.id === id);
    if (!snapshot) return;
    const versionSnapshot = s0.missionVersions[id];
    const wasSelected = s0.selectedMissionId === id;
    set((s) => ({
      missions: s.missions.filter((m) => m.id !== id),
      selectedMissionId: wasSelected ? null : s.selectedMissionId,
      placementMode: false,
      missionVersions: omitKey(s.missionVersions, id),
      missionSavingById: omitKey(s.missionSavingById, id),
      missionPendingSaveById: omitKey(s.missionPendingSaveById, id),
      missionConflictById: omitKey(s.missionConflictById, id),
      missionLastErrorById: omitKey(s.missionLastErrorById, id),
    }));
    const siteId = s0.currentSiteId;
    if (!siteId || !versionSnapshot) return; // temp-only mission — nothing to delete server-side
    void (async () => {
      try {
        await deleteDockMission(siteId, id, versionSnapshot);
      } catch (err) {
        if (useDockDemoStore.getState().currentSiteId !== siteId) return;
        if (err instanceof ApiError && err.status === 404) return; // already gone
        // Rollback for any other failure (including 409 — server delete
        // refused because version changed; user should refresh and retry).
        useDockDemoStore.setState((s) => {
          if (s.missions.some((m) => m.id === id)) return s;
          return {
            missions: [...s.missions, snapshot],
            selectedMissionId: wasSelected ? id : s.selectedMissionId,
            missionVersions: { ...s.missionVersions, [id]: versionSnapshot },
            missionLastErrorById: {
              ...s.missionLastErrorById,
              [id]: extractErrorMessage(err),
            },
          };
        });
      }
    })();
  },

  selectMission: (id) =>
    set({ selectedMissionId: id, placementMode: false }),

  updateMission: (id, patch) => {
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id === id ? { ...m, ...patch, updatedAt: new Date().toISOString() } : m,
      ),
    }));
    scheduleMissionSave(id);
  },

  setPlacementMode: (on) => set({ placementMode: on }),

  addWaypoint: (missionId, lng, lat) => {
    set((s) => ({
      missions: s.missions.map((m) => {
        if (m.id !== missionId) return m;
        const isFirst = m.waypoints.length === 0;
        const wp: DemoWaypoint = {
          id: genId('wp'),
          lng,
          lat,
          alt: m.defaultAlt ?? 30,
          gimbalPitch: m.defaultGimbalPitch ?? -45,
          action: 'none',
          photoPayload: ['wide', 'ir'],
          ...(isFirst && m.defaultLegParams
            ? { legParams: { ...m.defaultLegParams } }
            : {}),
        };
        return {
          ...m,
          waypoints: [...m.waypoints, wp],
          updatedAt: new Date().toISOString(),
        };
      }),
    }));
    scheduleMissionSave(missionId);
  },

  updateWaypoint: (missionId, waypointId, patch) => {
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id !== missionId
          ? m
          : {
              ...m,
              waypoints: m.waypoints.map((w) =>
                w.id === waypointId ? { ...w, ...patch } : w,
              ),
              updatedAt: new Date().toISOString(),
            },
      ),
    }));
    scheduleMissionSave(missionId);
  },

  updateWaypointLegParams: (missionId, waypointId, patch) => {
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id !== missionId
          ? m
          : {
              ...m,
              waypoints: m.waypoints.map((w) =>
                w.id === waypointId
                  ? { ...w, legParams: { ...(w.legParams ?? {}), ...patch } }
                  : w,
              ),
              updatedAt: new Date().toISOString(),
            },
      ),
    }));
    scheduleMissionSave(missionId);
  },

  deleteWaypoint: (missionId, waypointId) => {
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id !== missionId
          ? m
          : {
              ...m,
              waypoints: m.waypoints.filter((w) => w.id !== waypointId),
              updatedAt: new Date().toISOString(),
            },
      ),
    }));
    scheduleMissionSave(missionId);
  },

  reorderWaypoint: (missionId, fromIndex, toIndex) => {
    set((s) => ({
      missions: s.missions.map((m) => {
        if (m.id !== missionId) return m;
        if (fromIndex < 0 || fromIndex >= m.waypoints.length) return m;
        if (toIndex < 0 || toIndex >= m.waypoints.length) return m;
        const next = [...m.waypoints];
        const [moved] = next.splice(fromIndex, 1);
        next.splice(toIndex, 0, moved);
        return { ...m, waypoints: next, updatedAt: new Date().toISOString() };
      }),
    }));
    scheduleMissionSave(missionId);
  },

  insertWaypointAt: (missionId, atIndex, lng, lat) => {
    const newId = genId('wp');
    let inserted = false;
    set((s) => ({
      missions: s.missions.map((m) => {
        if (m.id !== missionId) return m;
        const clamped = Math.max(0, Math.min(atIndex, m.waypoints.length));
        const inheritAlt = clamped > 0 ? m.waypoints[clamped - 1].alt : 30;
        const wp: DemoWaypoint = {
          id: newId,
          lng,
          lat,
          alt: inheritAlt,
          action: 'none',
        };
        const next = [
          ...m.waypoints.slice(0, clamped),
          wp,
          ...m.waypoints.slice(clamped),
        ];
        inserted = true;
        return { ...m, waypoints: next, updatedAt: new Date().toISOString() };
      }),
    }));
    if (inserted) scheduleMissionSave(missionId);
    return inserted ? newId : null;
  },

  duplicateWaypoint: (missionId, waypointId) => {
    const newId = genId('wp');
    let duplicated = false;
    set((s) => ({
      missions: s.missions.map((m) => {
        if (m.id !== missionId) return m;
        const idx = m.waypoints.findIndex((w) => w.id === waypointId);
        if (idx < 0) return m;
        const src = m.waypoints[idx];
        const { autoRouted: _autoRouted, ...rest } = src;
        void _autoRouted;
        const dup: DemoWaypoint = { ...rest, id: newId };
        const next = [
          ...m.waypoints.slice(0, idx + 1),
          dup,
          ...m.waypoints.slice(idx + 1),
        ];
        duplicated = true;
        return { ...m, waypoints: next, updatedAt: new Date().toISOString() };
      }),
    }));
    if (duplicated) scheduleMissionSave(missionId);
    return duplicated ? newId : null;
  },

  setHoveredWaypointId: (id) => set({ hoveredWaypointId: id }),
  setSelectedWaypointId: (id) => set({ selectedWaypointId: id }),
  setClipboardLegParams: (params) => set({ clipboardLegParams: params }),
  setContextMenuTarget: (target) => set({ contextMenuTarget: target }),

  addWaypointsBulk: (missionId, points) => {
    if (points.length === 0) return [];
    const newIds: string[] = [];
    set((s) => ({
      missions: s.missions.map((m) => {
        if (m.id !== missionId) return m;
        const startEmpty = m.waypoints.length === 0;
        const fresh: DemoWaypoint[] = points.map((p, idx) => {
          const id = genId('wp');
          newIds.push(id);
          const isFirstPlaced = startEmpty && idx === 0;
          return {
            id,
            lng: p.lng,
            lat: p.lat,
            alt: m.defaultAlt ?? 30,
            gimbalPitch: m.defaultGimbalPitch ?? -45,
            action: 'none',
            ...(isFirstPlaced && m.defaultLegParams
              ? { legParams: { ...m.defaultLegParams } }
              : {}),
          };
        });
        return {
          ...m,
          waypoints: [...m.waypoints, ...fresh],
          updatedAt: new Date().toISOString(),
        };
      }),
    }));
    if (newIds.length > 0) scheduleMissionSave(missionId);
    return newIds;
  },

  setScanDrawMode: (mode) =>
    set(
      mode !== null
        ? {
            scanDrawMode: mode,
            scanDrawVertices: [],
            placementMode: false,
            nfzDrawMode: false,
            nfzDrawVertices: [],
          }
        : { scanDrawMode: null, scanDrawVertices: [] },
    ),

  appendScanDrawVertex: (lng, lat) =>
    set((s) =>
      s.scanDrawMode !== null
        ? { scanDrawVertices: [...s.scanDrawVertices, [lng, lat]] }
        : s,
    ),

  clearScanDraft: () => set({ scanDrawMode: null, scanDrawVertices: [] }),

  setScanLineSpacingM: (m) =>
    set((s) => ({
      scanLineSpacingM: Number.isFinite(m)
        ? Math.max(3, Math.min(80, m))
        : s.scanLineSpacingM,
    })),
  setScanLineStepM: (m) =>
    set((s) => ({
      scanLineStepM: Number.isFinite(m)
        ? Math.max(2, Math.min(80, m))
        : s.scanLineStepM,
    })),

  setMissionImportedData: (missionId, { photos, videos, flightPath }) =>
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id === missionId
          ? {
              ...m,
              importedPhotos: photos,
              importedVideos: videos,
              importedFlightPath: flightPath,
              updatedAt: new Date().toISOString(),
            }
          : m,
      ),
    })),

  clearMissionImportedData: (missionId) =>
    set((s) => ({
      missions: s.missions.map((m) =>
        m.id === missionId
          ? {
              ...m,
              importedPhotos: undefined,
              importedVideos: undefined,
              importedFlightPath: undefined,
              updatedAt: new Date().toISOString(),
            }
          : m,
      ),
    })),

  setSelectedDemoPhoto: (id) => set({ selectedDemoPhotoId: id }),

  createDockStation: (name = 'Dock 1') => {
    const now = new Date().toISOString();
    const station: DemoDockStation = {
      id: genId('dock'),
      name,
      serial: '',
      model: 'dock2',
      lng: 0,
      lat: 0,
      drone: { model: 'M3TD', payload: ['rgb', 'thermal'] },
      defaults: {
        rthAltM: 50,
        maxAltM: 120,
        maxSpeedMps: 10,
        maxDistanceM: 1500,
      },
      geofence: { type: 'radius', radiusM: 1500 },
      geofenceVisible: true,
      operator: {},
      createdAt: now,
      updatedAt: now,
    };
    set((s) => {
      const dockStations = [...s.dockStations, station];
      const selectedDockStationId = station.id;
      persistDock({ dockStations, selectedDockStationId }, s.currentSiteId);
      return { dockStations, selectedDockStationId };
    });
    return station.id;
  },

  deleteDockStation: (id) =>
    set((s) => {
      const dockStations = s.dockStations.filter((d) => d.id !== id);
      const selectedDockStationId =
        s.selectedDockStationId === id ? null : s.selectedDockStationId;
      persistDock({ dockStations, selectedDockStationId }, s.currentSiteId);
      return { dockStations, selectedDockStationId };
    }),

  selectDockStation: (id) =>
    set((s) => {
      persistDock(
        { dockStations: s.dockStations, selectedDockStationId: id },
        s.currentSiteId,
      );
      return { selectedDockStationId: id };
    }),

  updateDockStation: (id, patch) =>
    set((s) => {
      const dockStations = s.dockStations.map((d) =>
        d.id === id ? { ...d, ...patch, updatedAt: new Date().toISOString() } : d,
      );
      persistDock(
        { dockStations, selectedDockStationId: s.selectedDockStationId },
        s.currentSiteId,
      );
      return { dockStations };
    }),

  setNfzDrawMode: (on) =>
    set(
      on
        ? {
            nfzDrawMode: true,
            nfzDrawVertices: [],
            nfzEditingId: null,
            placementMode: false,
          }
        : { nfzDrawMode: false, nfzDrawVertices: [] },
    ),

  appendNfzDrawVertex: (lng, lat) =>
    set((s) =>
      s.nfzDrawMode
        ? { nfzDrawVertices: [...s.nfzDrawVertices, [lng, lat]] }
        : s,
    ),

  clearNfzDraft: () => set({ nfzDrawMode: false, nfzDrawVertices: [] }),

  setNfzEditingId: (id) =>
    set(
      id !== null
        ? { nfzEditingId: id, nfzDrawMode: false, nfzDrawVertices: [] }
        : { nfzEditingId: null },
    ),

  addNoFlyZone: (dockId, vertices, name) => {
    if (vertices.length < 3) return null;
    const nfzId = genId('nfz');
    set((s) => {
      const dockStations = s.dockStations.map((d) => {
        if (d.id !== dockId) return d;
        const existing = d.noFlyZones ?? [];
        const nfz: DemoNoFlyZone = {
          id: nfzId,
          name: name ?? `No-fly zone ${existing.length + 1}`,
          vertices,
          visible: true,
        };
        return {
          ...d,
          noFlyZones: [...existing, nfz],
          updatedAt: new Date().toISOString(),
        };
      });
      persistDock(
        { dockStations, selectedDockStationId: s.selectedDockStationId },
        s.currentSiteId,
      );
      return {
        dockStations,
        nfzDrawMode: false,
        nfzDrawVertices: [],
      };
    });
    return nfzId;
  },

  updateNoFlyZone: (dockId, nfzId, patch) =>
    set((s) => {
      const dockStations = s.dockStations.map((d) => {
        if (d.id !== dockId) return d;
        const existing = d.noFlyZones ?? [];
        const nextZones = existing.map((z) =>
          z.id === nfzId ? { ...z, ...patch } : z,
        );
        return {
          ...d,
          noFlyZones: nextZones,
          updatedAt: new Date().toISOString(),
        };
      });
      persistDock(
        { dockStations, selectedDockStationId: s.selectedDockStationId },
        s.currentSiteId,
      );
      return { dockStations };
    }),

  deleteNoFlyZone: (dockId, nfzId) =>
    set((s) => {
      const dockStations = s.dockStations.map((d) => {
        if (d.id !== dockId) return d;
        const existing = d.noFlyZones ?? [];
        return {
          ...d,
          noFlyZones: existing.filter((z) => z.id !== nfzId),
          updatedAt: new Date().toISOString(),
        };
      });
      persistDock(
        { dockStations, selectedDockStationId: s.selectedDockStationId },
        s.currentSiteId,
      );
      return {
        dockStations,
        nfzEditingId: s.nfzEditingId === nfzId ? null : s.nfzEditingId,
      };
    }),

  removeAutoRoutedWaypoints: (missionId) => {
    let removedCount = 0;
    set((s) => {
      const missions = s.missions.map((m) => {
        if (m.id !== missionId) return m;
        const filtered = m.waypoints.filter((w) => !w.autoRouted);
        removedCount = m.waypoints.length - filtered.length;
        if (removedCount === 0) return m;
        return {
          ...m,
          waypoints: filtered,
          updatedAt: new Date().toISOString(),
        };
      });
      return removedCount === 0 ? s : { missions };
    });
    if (removedCount > 0) scheduleMissionSave(missionId);
    return removedCount;
  },

  openLiveView: (missionId) => set({ liveViewMissionId: missionId }),

  closeLiveView: () =>
    set((s) => {
      const closingId = s.liveViewMissionId;
      const missions = closingId
        ? s.missions.map((m) =>
            m.id === closingId && m.flowState === 'in_flight'
              ? {
                  ...m,
                  flowState: 'completed' as const,
                  updatedAt: new Date().toISOString(),
                }
              : m,
          )
        : s.missions;
      return { missions, liveViewMissionId: null };
    }),

  sendMissionToOperator: (missionId) =>
    set((s) => {
      const now = Date.now();
      const missions = s.missions.map((m) =>
        m.id === missionId
          ? {
              ...m,
              flowState: 'pending_approval' as const,
              sentAt: now,
              scheduledStartAt: undefined,
              mockDataImportedAt: undefined,
              updatedAt: new Date().toISOString(),
            }
          : m,
      );
      return { missions };
    }),

  cancelMissionFlow: (missionId) =>
    set((s) => {
      const missions = s.missions.map((m) =>
        m.id === missionId
          ? {
              ...m,
              flowState: 'draft' as const,
              sentAt: undefined,
              scheduledStartAt: undefined,
              updatedAt: new Date().toISOString(),
            }
          : m,
      );
      return { missions };
    }),

  setMissionFlowState: (missionId, state, extras) =>
    set((s) => {
      const missions = s.missions.map((m) => {
        if (m.id !== missionId) return m;
        const next: DemoMission = {
          ...m,
          flowState: state,
          updatedAt: new Date().toISOString(),
        };
        if (extras && 'scheduledStartAt' in extras) {
          next.scheduledStartAt = extras.scheduledStartAt ?? undefined;
        }
        return next;
      });
      return { missions };
    }),

  clearAll: () =>
    set((s) => {
      // Wipe the active project's dock-stations bucket. Missions are not
      // touched server-side — clearAll is local-only "reset this device's
      // demo state" — but the in-memory mission list IS cleared so the
      // user sees an empty planner. Subsequent setActiveProject will
      // re-fetch the server-side mission rows.
      if (s.currentSiteId !== null) {
        try {
          localStorage.removeItem(storageKey(s.currentSiteId));
        } catch {
          // ignore
        }
      }
      return {
        dockStations: [],
        selectedDockStationId: null,
        ...INITIAL_MISSION_STATE,
        missionsLoaded: true,
        ...INITIAL_TRANSIENT,
        currentSiteId: s.currentSiteId,
      };
    }),
}));
